import { ProcessTypes, UserWorkoutSession } from 'fitbeat.gym-core';
import { BaseUserProfile, IDay, WorkoutType } from 'fitbeat.models';
import React, { FunctionComponent } from 'react';
import { translate } from 'react-i18next';
import {
  ActivityIndicator, Platform, StyleSheet, View,
} from 'react-native';
import { connect } from 'react-redux';
import { Button, ButtonSize, ButtonTypes } from '../../components';
import Theme from '../../theme';
import { FormError } from '../../ui';
import { defaultScale, i18n } from '../../utils';
import {
  getNextBookingStep as getNextBookingStepAction,
  getPreviousBookingStep as getPreviousBookingStepAction,
  makeBooking as makeBookingAction,
  setDuration as setDurationAction,
  startWorkoutNowButtonInBookingWizardPressed,
} from '../actions';
import {
  BookingFlowFooter, BookingInstructionHeader,
  BookingInstructionSubtitle,
} from '../components';
import { ClassDurationSelection } from '../components/ClassDurationSelection';
import { ButtonMargin } from '../components/SelectionContainer';
import { styles as sharedStyles } from './Styles';

export interface IProps {
  userProfile: BaseUserProfile;
  workoutSession: UserWorkoutSession;
  workoutSessionService: any;
  workoutType: WorkoutType;
  makeBookingLoading: boolean;
  currentSelectedGymName: string;
  makeBookingError: string | null;
  showStartOrScheduleOnDurationStep: boolean;
  pendingSlot: any;
  isOfficeScheduleBooking: boolean;
  makeBooking: (day: IDay) => void;
  day: IDay;
  setDuration(workoutType: WorkoutType): void;
  t(key: string, options?: any): string;
  getNextBookingStep(): void;
  startWorkoutNowButtonPressed(): void;
  getPreviousBookingStep(): void;
}

const ClassDuration: FunctionComponent<IProps> = ({
  t,
  workoutType,
  setDuration,
  getNextBookingStep,
  makeBookingLoading,
  makeBookingError,
  startWorkoutNowButtonPressed,
  pendingSlot,
  isOfficeScheduleBooking,
  day,
  makeBooking,
  getPreviousBookingStep,
  showStartOrScheduleOnDurationStep,
}) => {
  const getNextBookingStepForGym = () => {
    const isInstant = pendingSlot && pendingSlot.isInstant;
    if (isInstant && Platform.OS !== 'web') {
      makeBooking(day);
    } else {
      getNextBookingStep();
    }
  };

  if (makeBookingLoading) {
    return (
      <ActivityIndicator
        size='small'
        animating
        color={Theme.PRIMARY}
        style={{ flex: 1 }}
      />
    );
  }

  const showOnlyStartNowButton = isOfficeScheduleBooking;
  const showStartNowOrScheduleButtons = Platform.OS !== 'web'
    && (showStartOrScheduleOnDurationStep || showOnlyStartNowButton);

  return (
    <View style={styles.container}>
      <View style={sharedStyles.body}>
        <BookingInstructionHeader header={t('selectClassDuration')} />
        <ClassDurationSelection
          onPress={setDuration}
          workoutType={workoutType}
        />
        {workoutType
          && (
          <BookingInstructionSubtitle header={
            t('classDurationDescription', {
              context: workoutType.toString(),
            })
}
          />
          )}
      </View>
      <BookingFlowFooter>
        {makeBookingError && <FormError error={new Error(makeBookingError)} />}

        {!showStartNowOrScheduleButtons
          && (
          <View style={styles.actions}>
            <Button
              fontColor={Theme.PRIMARY}
              size={ButtonSize.MEDIUM}
              buttonType={ButtonTypes.OUTLINE}
              title={i18n.t('bookings:back')}
              onPress={getPreviousBookingStep}
              containerStyle={styles.backContainer}
            />

            <Button
              fontColor={Theme.WHITE}
              size={ButtonSize.MEDIUM}
              disabled={!workoutType}
              title={i18n.t('bookings:continue')}
              onPress={getNextBookingStepForGym}
              containerStyle={styles.nextContainer}
            />
          </View>
          )}

        {showStartNowOrScheduleButtons
          && (
          <View>
            {!showOnlyStartNowButton && (
              <Button
                fontColor={Theme.PRIMARY}
                size={ButtonSize.MEDIUM}
                buttonType={ButtonTypes.OUTLINE}
                disabled={!workoutType}
                title={i18n.t('bookings:scheduleForLater')}
                onPress={getNextBookingStep}
                containerStyle={{ marginBottom: defaultScale(ButtonMargin) }}
              />
            )}
            <Button
              fontColor={Theme.WHITE}
              size={ButtonSize.MEDIUM}
              disabled={!workoutType}
              title={i18n.t('bookings:StartWorkoutNow')}
              onPress={startWorkoutNowButtonPressed}
            />
          </View>
          )}
      </BookingFlowFooter>
    </View>
  );
};

const mapStateToProps = (state): Partial<IProps> => ({
  currentSelectedGymName: state.bookings.bookingWizard.currentSelectedGymName,
  workoutType: state.bookings.bookingWizard.workoutType,
  workoutSession: state.bookings.bookingWizard.workoutSession,
  makeBookingLoading: state.bookings.bookingWizard.makeBookingLoading,
  makeBookingError: state.bookings.bookingWizard.makeBookingError,
  showStartOrScheduleOnDurationStep: state.bookings.bookingWizard.showStartOrScheduleOnDurationStep,
  pendingSlot: state.bookings.bookingWizard.pendingSlot,
  isOfficeScheduleBooking: state.bookings.bookingWizard.isOfficeScheduleBooking,
});

const mapDispatchToProps = (dispatch, ownProps) => {
  const { workoutSessionService, userProfile, waitingListServiceClient } = ownProps;
  return {
    getNextBookingStep: () => dispatch(getNextBookingStepAction(userProfile)),
    makeBooking: (day) => dispatch(makeBookingAction(workoutSessionService, userProfile, day)),
    setDuration: (workoutType: WorkoutType) => dispatch(setDurationAction(workoutType)),
    startWorkoutNowButtonPressed: () => dispatch(
      startWorkoutNowButtonInBookingWizardPressed(workoutSessionService, waitingListServiceClient, userProfile),
    ),
    getPreviousBookingStep: () => dispatch(getPreviousBookingStepAction()),
  };
};

export const ClassDurationStep = connect(mapStateToProps, mapDispatchToProps)(
  translate(['bookings', 'common'], { wait: true })(ClassDuration),
);

ClassDurationStep.getTitle = () => <></>;

const styles = StyleSheet.create({
  container: {
    flex: 1,
    justifyContent: 'flex-end',
  },
  actions: {
    flexDirection: 'row',
  },
  backContainer: {
    flex: 0.3,
  },
  nextContainer: {
    flex: 0.7,
    marginLeft: 5,
  },
});
